import React, { useState } from "react"
import { useIntl, FormattedMessage } from "gatsby-plugin-intl"
import { Button, TextInput, TextEditor } from "@tmu/components/common"
import styled from "styled-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faRedo, faTimes } from "@fortawesome/pro-regular-svg-icons"

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 500px;
  max-width: 100%;
`

const HeaderSection = styled.div`
  margin-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Branding = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const LogoSphere = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #00d2ff 0%, #3a7bd5 100%);
  box-shadow: 0 4px 10px rgba(58, 123, 213, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 6px;
    left: 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 70%);
  }
`

const Title = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`

const CloseButton = styled.button`
  background: transparent;
  color: #aaa;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f5f5f5;
    color: #666;
  }
`

const FieldLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  display: block;
`

const ToneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
`

const ToneSelector = styled.select`
  padding: 8px 12px;
  border-radius: 0.25rem;
  border: 1px solid #ddd;
  font-size: 0.85rem;
  flex: 1;
`

const FooterActions = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TrustAIPreview = ({ title, content, onApprove, onRegenerate, onCancel, onClose }) => {
  const { formatMessage } = useIntl()
  const [editableTitle, setEditableTitle] = useState(title)
  const [editableContent, setEditableContent] = useState(content)
  const [tone, setTone] = useState("EMOTIONAL")

  return (
    <PreviewContainer>
      <HeaderSection>
        <TitleRow>
          <Branding>
            <LogoSphere />
            <Title>{formatMessage({ id: "trustai::studio::title", defaultMessage: "TrustAI Studio" })}</Title>
          </Branding>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} size="lg" />
          </CloseButton>
        </TitleRow>
      </HeaderSection>

      <div style={{ marginBottom: '12px' }}>
        <FieldLabel>
          {formatMessage({ id: "trustai::preview.titleLabel", defaultMessage: "Campaign Title" })}
        </FieldLabel>
        <TextInput
          newDesign
          value={editableTitle}
          onChange={e => setEditableTitle(e.target.value)}
          placeholder={formatMessage({ id: "trustai::preview.titlePlaceholder", defaultMessage: "Campaign title" })}
        />
      </div>

      <div>
        <FieldLabel>
          {formatMessage({ id: "trustai::preview.descriptionLabel", defaultMessage: "Campaign Description" })}
        </FieldLabel>
        <TextEditor
          value={editableContent}
          onChange={setEditableContent}
          height={200}
        />
      </div>

      <ToneRow>
        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
          {formatMessage({ id: "trustai::preview.style", defaultMessage: "Style:" })}
        </span>
        <ToneSelector value={tone} onChange={e => setTone(e.target.value)}>
          <option value="EMOTIONAL">{formatMessage({ id: "trustai::tone::emotional", defaultMessage: "Emotional" })}</option>
          <option value="URGENT">{formatMessage({ id: "trustai::tone::urgent", defaultMessage: "Urgent" })}</option>
          <option value="PROFESSIONAL">{formatMessage({ id: "trustai::tone::professional", defaultMessage: "Professional" })}</option>
          <option value="FORMAL">{formatMessage({ id: "trustai::tone::formal", defaultMessage: "Formal" })}</option>
        </ToneSelector>
        <Button variant="outlined" size="small" onClick={() => onRegenerate(tone)}>
          <FontAwesomeIcon icon={faRedo} /> {formatMessage({ id: "trustai::preview.regenerate", defaultMessage: "Re-generate" })}
        </Button>
      </ToneRow>

      <FooterActions style={{ gap: '12px' }}>
        <Button variant="outlined" style={{ flex: 1 }} onClick={onCancel}>
          <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
          {formatMessage({ id: "trustai::preview.reject", defaultMessage: "Reject" })}
        </Button>
        <Button color="blue" style={{ flex: 1 }} onClick={() => onApprove(editableTitle, editableContent, 'all')}>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: '5px' }} />
          {formatMessage({ id: "trustai::preview.approve_single", defaultMessage: "Approve & Insert" })}
        </Button>


      </FooterActions>
    </PreviewContainer>
  )
}

export default TrustAIPreview
